import * as React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { MarketingNav } from './MarketingNav';
import { MarketingFooter } from './MarketingFooter';
import { getSeoSettings, DEFAULT_SEO, type SeoSettings } from './api';

/**
 * MarketingLayout
 * --------------------------------------------------------------
 * Top-level shell for every marketing page. Renders:
 *   1. AnnouncementBar  — thin promo strip
 *   2. MarketingNav     — sticky top navigation
 *   3. <Outlet />       — the active marketing route
 *   4. MarketingFooter  — mega-footer
 *
 * Also responsible for:
 *   - Fetching `/api/public/site/seo` once on mount and applying
 *     the defaults to <title>, meta description, OG, Twitter,
 *     robots, canonical.
 *   - Injecting GTM / GA4 script tags into <head> when configured
 *     (one-time, deduped by id).
 *   - Exposing the loaded SEO settings via React context so
 *     `MarketingPageHead` can compose per-page overrides.
 *
 * The shell respects light + dark mode and supplies the page
 * background, so individual pages can stay focused on content.
 */

/* ──────────────────────────────────────────────────────────────
 * SEO context — consumed by <MarketingPageHead /> on each page
 * ──────────────────────────────────────────────────────────── */

const SeoContext = React.createContext<SeoSettings | null>(null);

export function useMarketingSeo(): SeoSettings | null {
  return React.useContext(SeoContext);
}

export function MarketingLayout() {
  const [seo, setSeo] = React.useState<SeoSettings | null>(null);
  const location = useLocation();

  // Fetch SEO defaults once. Falls back to DEFAULT_SEO on failure.
  React.useEffect(() => {
    let cancelled = false;
    getSeoSettings()
      .then((s) => {
        if (!cancelled) setSeo(s);
      })
      .catch(() => {
        if (!cancelled) setSeo({ ...DEFAULT_SEO });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply defaults to <head> as soon as SEO loads. Per-page overrides
  // run their own effect via <MarketingPageHead /> after this.
  React.useEffect(() => {
    if (!seo) return;
    applySeoToDocument(seo, undefined);
  }, [seo]);

  // Inject GTM / GA4 scripts once when SEO settings arrive.
  React.useEffect(() => {
    if (!seo) return;
    if (seo.gtm_id) injectGtm(seo.gtm_id);
    if (seo.ga4_id) injectGa4(seo.ga4_id);
  }, [seo]);

  // Update canonical on route change (so /pricing → /features bumps the canonical href).
  React.useEffect(() => {
    if (!seo) return;
    setLinkAttr('canonical', currentCanonical());
  }, [seo, location.pathname]);

  return (
    <SeoContext.Provider value={seo}>
      <div className="relative min-h-screen flex flex-col bg-pearl-50 text-ink dark:bg-ink-900 dark:text-white">
        {/* Soft ambient background — leaks through for depth without distracting. */}
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-10 bg-pds-mesh opacity-80 dark:opacity-60"
        />

        <AnnouncementBar />
        <MarketingNav />

        <main className="flex-1">
          <Outlet />
        </main>

        <MarketingFooter />
      </div>
    </SeoContext.Provider>
  );
}

/* ──────────────────────────────────────────────────────────────
 * AnnouncementBar
 * ──────────────────────────────────────────────────────────── */

function AnnouncementBar() {
  return (
    <div className="relative z-30 bg-ink-900 text-white dark:bg-black dark:border-b dark:border-white/5">
      <div className="max-w-site mx-auto px-6 lg:px-8 h-9 flex items-center justify-center gap-2 text-[12px]">
        <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 bg-coral text-white text-[10px] font-bold uppercase tracking-[0.10em] rounded-full">
          <Sparkles className="h-2.5 w-2.5" />
          New
        </span>
        <span className="text-white/85 font-medium truncate">
          Trial just got better: no card to start. 7 days free.
        </span>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1 text-white hover:text-coral underline-offset-2 hover:underline font-semibold whitespace-nowrap"
        >
          See pricing
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
 * DOM helpers — imperative <head> mutation
 *
 * Exported so MarketingPageHead can re-apply defaults on unmount.
 * ──────────────────────────────────────────────────────────── */

/** Optional article metadata — emitted as article:* OG tags on blog posts / docs. */
export interface ArticleMeta {
  ogType?: 'website' | 'article';
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
}

export function applySeoToDocument(
  seo: SeoSettings,
  pagePath?: string,
  article?: ArticleMeta,
): void {
  if (typeof document === 'undefined') return;

  document.title = seo.default_title || DEFAULT_SEO.default_title;

  setMetaName('description', seo.default_description);
  setMetaName('robots', seo.default_robots || 'index, follow');

  const ogType = article?.ogType || 'website';
  setMetaProp('og:type', ogType);
  setMetaProp('og:title', seo.default_title);
  setMetaProp('og:description', seo.default_description);
  setMetaProp('og:url', currentCanonical(pagePath));
  setMetaProp('og:site_name', 'Worqly');
  if (seo.default_og_image_url) setMetaProp('og:image', seo.default_og_image_url);

  // article:* tags — set when this is an article, otherwise removed so they
  // never leak onto a subsequently-viewed non-article route (SPA navigation).
  if (ogType === 'article') {
    setMetaProp('article:published_time', article?.articlePublishedTime);
    setMetaProp('article:modified_time', article?.articleModifiedTime);
    setMetaProp('article:author', article?.articleAuthor);
  } else {
    removeMetaProp('article:published_time');
    removeMetaProp('article:modified_time');
    removeMetaProp('article:author');
  }

  setMetaName('twitter:card', seo.default_og_image_url ? 'summary_large_image' : 'summary');
  setMetaName('twitter:title', seo.default_title);
  setMetaName('twitter:description', seo.default_description);
  if (seo.default_og_image_url) setMetaName('twitter:image', seo.default_og_image_url);
  if (seo.default_twitter_handle) setMetaName('twitter:site', seo.default_twitter_handle);

  setLinkAttr('canonical', currentCanonical(pagePath));
}

function setMetaName(name: string, content: string | null | undefined): void {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${cssEscape(name)}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaProp(property: string, content: string | null | undefined): void {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[property="${cssEscape(property)}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMetaProp(property: string): void {
  if (typeof document === 'undefined') return;
  const el = document.head.querySelector<HTMLMetaElement>(
    `meta[property="${cssEscape(property)}"]`,
  );
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

function setLinkAttr(rel: string, href: string | null | undefined): void {
  if (!href) return;
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${cssEscape(rel)}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function currentCanonical(pagePath?: string): string {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  const path = pagePath ?? window.location.pathname;
  return `${origin}${path}`;
}

function cssEscape(s: string): string {
  // Minimal CSS escape — we only ever pass plain ascii names like "og:image".
  return s.replace(/"/g, '\\"');
}

/* ──────────────────────────────────────────────────────────────
 * GTM / GA4 injection — one-time, deduped by id
 * ──────────────────────────────────────────────────────────── */

const INJECTED = new Set<string>();

function injectGtm(gtmId: string): void {
  if (typeof document === 'undefined') return;
  const key = `gtm:${gtmId}`;
  if (INJECTED.has(key)) return;
  INJECTED.add(key);

  const script = document.createElement('script');
  script.setAttribute('data-worqly-gtm', gtmId);
  script.text =
    `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':` +
    `new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],` +
    `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=` +
    `'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);` +
    `})(window,document,'script','dataLayer','${gtmId}');`;
  document.head.appendChild(script);
}

function injectGa4(gaId: string): void {
  if (typeof document === 'undefined') return;
  const key = `ga4:${gaId}`;
  if (INJECTED.has(key)) return;
  INJECTED.add(key);

  const loader = document.createElement('script');
  loader.async = true;
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
  loader.setAttribute('data-worqly-ga4', gaId);
  document.head.appendChild(loader);

  const init = document.createElement('script');
  init.text =
    `window.dataLayer = window.dataLayer || [];` +
    `function gtag(){dataLayer.push(arguments);}` +
    `gtag('js', new Date());` +
    `gtag('config', '${gaId}');`;
  init.setAttribute('data-worqly-ga4-init', gaId);
  document.head.appendChild(init);
}
